"use client";

import React from "react";
import {
  Box,
  Flex,
  HStack,
  Button,
  useToast,
  Select,
  Tabs,
  TabList,
  Tab,
  Text,
  Grid,
  VStack,
  IconButton,
  useDisclosure,
  Spinner,
  Badge,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Phone, Trash2, PhoneCall, ShoppingCart, Cable } from "lucide-react";
import { motion } from "framer-motion";
import VoiceAgents from "../VoiceAgents";
import VoiceKnowledgeBase from "./VoiceKnowledgeBase";
import {
  phoneNumberService,
  type PhoneNumber,
} from "../../voice-services/phoneNumberService";
import {
  getDisplayPhonePretty,
  getVoicePhoneCardTitle,
  shouldShowVoicePhoneCardSubtitle,
} from "../../voice-services/voicePhoneDisplay";
import {
  hasOutboundAgent,
  primaryInboundAgentId,
  primaryOutboundAgentId,
} from "../../voice-services/phoneNumberAgents";
import { agentService, type Agent } from "../../voice-services/agentService";
import { CreatePhoneNumberDialog } from "../voice-phone-numbers/CreatePhoneNumberDialog";
import { ImportPhoneNumberDialog } from "../voice-phone-numbers/ImportPhoneNumberDialog";
import { ConnectVoiceNumberDialog } from "../voice-phone-numbers/ConnectVoiceNumberDialog";
import { PhoneNumberDetails } from "../voice-phone-numbers/PhoneNumberDetails";
import { MakeOutboundCallDialog } from "../voice-phone-numbers/MakeOutboundCallDialog";
import { useAuth } from "../../contexts/AuthContext";
import { useStableCredits } from "../../hooks/useStableCredits";

const MotionBox = motion(Box);

// Match the optimizations pattern from VoiceAgents
const CACHE_TTL_MS = 5 * 60 * 1000;

type CacheEnvelope<T> = { data: T; timestamp: number };

const VoicePhoneNumbers = () => {
  const { isAdmin, currentClientId, clients } = useAuth();
  const credits = useStableCredits();

  const [selectedVoiceTab, setSelectedVoiceTab] =
    React.useState<string>("phone-numbers");
  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentType") || "Voice";
    }
  );

  const [phoneNumbers, setPhoneNumbers] = React.useState<PhoneNumber[]>([]);
  const [agents, setAgents] = React.useState<Agent[]>([]);

  const [isLoading, setIsLoading] = React.useState(true);

  const [selectedPhoneNumber, setSelectedPhoneNumber] =
    React.useState<PhoneNumber | null>(null);
  const [phoneNumberToDelete, setPhoneNumberToDelete] =
    React.useState<PhoneNumber | null>(null);
  const [phoneNumberToCall, setPhoneNumberToCall] =
    React.useState<PhoneNumber | null>(null);

  const toast = useToast();
  const navigate = useNavigate();

  const {
    isOpen: isCreateDialogOpen,
    onOpen: onCreateDialogOpen,
    onClose: onCreateDialogClose,
  } = useDisclosure();
  const {
    isOpen: isImportDialogOpen,
    onOpen: onImportDialogOpen,
    onClose: onImportDialogClose,
  } = useDisclosure();
  const {
    isOpen: isEditModalOpen,
    onOpen: onEditModalOpen,
    onClose: onEditModalClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();
  const {
    isOpen: isCallDialogOpen,
    onOpen: onCallDialogOpen,
    onClose: onCallDialogClose,
  } = useDisclosure();
  const {
    isOpen: isConnectOpen,
    onOpen: onConnectOpen,
    onClose: onConnectClose,
  } = useDisclosure();

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  // --- Tenant-aware companyName (same concept as VoiceAgents) ---
  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient ? currentClient.company_name : undefined;
  }, [isAdmin, currentClientId, clients]);

  const canBuyNumbers = ["LeadiumX", "Collective Solar"].includes(companyName ?? "");

  // --- Abort controller to cancel in-flight requests on tenant changes/unmount ---
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // --- Cache helpers (tenant scoped) ---
  const getCacheKey = React.useCallback(
    (key: string) => `voice_phone_numbers_${key}_${companyName || "self"}`,
    [companyName]
  );

  const loadFromCache = React.useCallback(
    <T,>(key: string): T | null => {
      try {
        const raw = localStorage.getItem(getCacheKey(key));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as CacheEnvelope<T>;
        const age = Date.now() - (parsed.timestamp || 0);
        if (age > CACHE_TTL_MS) return null;
        return parsed.data ?? null;
      } catch {
        return null;
      }
    },
    [getCacheKey]
  );

  const saveToCache = React.useCallback(
    (key: string, data: any) => {
      try {
        localStorage.setItem(
          getCacheKey(key),
          JSON.stringify({
            data,
            timestamp: Date.now(),
          } satisfies CacheEnvelope<any>)
        );
      } catch {
        // ignore quota / serialization errors
      }
    },
    [getCacheKey]
  );

  // --- On tenant switch: abort + clear UI + decide whether to show big loader ---
  React.useEffect(() => {
    // abort any in-flight request for previous tenant
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Clear previous tenant UI to prevent flashing wrong data
    setPhoneNumbers([]);
    setAgents([]);

    // Close potentially open dialogs/modals to avoid cross-tenant weirdness
    // (not required, but safe)
    setSelectedPhoneNumber(null);
    setPhoneNumberToDelete(null);
    setPhoneNumberToCall(null);

    // Only show the big loader if nothing cached for this tenant
    const hasCachedNumbers = !!loadFromCache<PhoneNumber[]>("phone_numbers");
    const hasCachedAgents = !!loadFromCache<Agent[]>("agents");

    setIsLoading(!(hasCachedNumbers || hasCachedAgents));
  }, [companyName, loadFromCache]);

  // --- Main fetch (cache-first, parallel, abort-safe) ---
  const loadData = React.useCallback(async () => {
    // Abort any in-flight requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // 1) Hydrate from cache immediately for instant UI
    const cachedNumbers = loadFromCache<PhoneNumber[]>("phone_numbers");
    const cachedAgents = loadFromCache<Agent[]>("agents");

    if (cachedNumbers) setPhoneNumbers(cachedNumbers);
    if (cachedAgents) setAgents(cachedAgents);

    // If we had cached data, avoid forcing a full-screen loader while we revalidate.
    // Keep current isLoading state as-is unless we truly have nothing.
    if (!cachedNumbers && !cachedAgents && !signal.aborted) {
      setIsLoading(true);
    }

    try {
      // 2) Fetch fresh data in parallel (core)
      const [numbersData, agentsData] = await Promise.all([
        phoneNumberService.list(companyName),
        agentService.list(companyName),
      ]);

      if (signal.aborted) return;

      setPhoneNumbers(numbersData);
      setAgents(agentsData);

      saveToCache("phone_numbers", numbersData);
      saveToCache("agents", agentsData);
    } catch (error: any) {
      if (error?.name === "AbortError") return;

      console.error("Error loading data:", error);
      toast({
        title: "Error loading data",
        description: "Failed to load phone numbers and agents",
        status: "error",
        duration: 3000,
      });
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  }, [companyName, loadFromCache, saveToCache, toast]);

  React.useEffect(() => {
    loadData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [loadData]);

  // --- Tab state sync (unchanged logic) ---
  React.useEffect(() => {
    const savedTab = localStorage.getItem("currentAgentTab") || "phone-numbers";
    setSelectedVoiceTab(savedTab);

    const handleTabChange = (event: any) => {
      setSelectedVoiceTab(event.detail);
      localStorage.setItem("currentAgentTab", event.detail);
    };

    window.addEventListener("agentTabChange", handleTabChange);
    return () => {
      window.removeEventListener("agentTabChange", handleTabChange);
    };
  }, []);

  React.useEffect(() => {
    const handleAgentTypeChangeEvt = (event: any) => {
      const newType = localStorage.getItem("currentAgentType") || "Voice";
      if (newType !== selectedAgentType) {
        setSelectedAgentType(newType);
      }
    };

    window.addEventListener("agentTypeChange", handleAgentTypeChangeEvt);
    return () => {
      window.removeEventListener("agentTypeChange", handleAgentTypeChangeEvt);
    };
  }, [selectedAgentType]);

  const handleTabChange = (newTab: string) => {
    setSelectedVoiceTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);
    if (type === "All") {
      navigate("/all-agents");
    } else if (type === "Voice") {
      navigate("/voice-agents");
    } else if (type === "SMS") {
      navigate("/sms-agents");
    } else if (type === "Email") {
      navigate("/email-agents");
    } else if (type === "WhatsApp") {
      navigate("/whatsapp-agents");
    }
  };

  const handlePhoneNumberClick = (phoneNumber: PhoneNumber) => {
    setSelectedPhoneNumber(phoneNumber);
    onEditModalOpen();
  };

  const handleDeleteClick = (phoneNumber: PhoneNumber, e: React.MouseEvent) => {
    e.stopPropagation();
    setPhoneNumberToDelete(phoneNumber);
    onDeleteAlertOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!phoneNumberToDelete) return;

    try {
      await phoneNumberService.delete(phoneNumberToDelete.phone_number);
      toast({
        title: "Phone number deleted",
        status: "success",
        duration: 3000,
      });

      // Refresh (keeps SWR behavior)
      loadData();

      onDeleteAlertClose();
      setPhoneNumberToDelete(null);
    } catch (error: any) {
      toast({
        title: "Error deleting phone number",
        description:
          error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleCallClick = (phoneNumber: PhoneNumber, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasOutboundAgent(phoneNumber)) {
      toast({
        title: "No outbound agent configured",
        description: "Please configure an outbound agent first",
        status: "warning",
        duration: 3000,
      });
      return;
    }
    setPhoneNumberToCall(phoneNumber);
    onCallDialogOpen();
  };

  const handlePhoneNumberChange = () => {
    // Revalidate everything after mutations
    loadData();
    onCreateDialogClose();
    onImportDialogClose();
    onEditModalClose();
  };

  // Delegate to other screens (unchanged)
  if (selectedAgentType === "Voice") {
    if (selectedVoiceTab === "agents") {
      return <VoiceAgents />;
    }
    if (selectedVoiceTab === "knowledge-base") {
      return <VoiceKnowledgeBase />;
    }
  }

  const getTabIndex = () => {
    switch (selectedVoiceTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 1;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    handleTabChange(tabs[index]);
  };

  const getAgentName = (agentId: string | null) => {
    if (!agentId) return "None";
    const agent = agents.find((a) => a.agent_id === agentId);
    return agent?.agent_name || agentId;
  };

  const totalPhoneNumbers = phoneNumbers.length;
  const activeInbound = phoneNumbers.filter((p) =>
    primaryInboundAgentId(p),
  ).length;
  const activeOutbound = phoneNumbers.filter((p) =>
    primaryOutboundAgentId(p),
  ).length;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg: selectedVoiceTab === "agents" ? "white" : "gray.200",
                }}
              >
                🤖 Voice Agents
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedVoiceTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📞 Voice Phone Numbers
              </Tab>
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{
                  bg: "white",
                  color: "black",
                  boxShadow: "sm",
                }}
                _hover={{
                  bg:
                    selectedVoiceTab === "knowledge-base"
                      ? "white"
                      : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{
                borderColor: "primary.400",
                shadow: "md",
              }}
              _focus={{
                borderColor: "primary.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>

              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}

              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>

          <Menu>
            <MenuButton
              as={Button}
              leftIcon={<PhoneCall size={16} />}
              variant="brandGradient"
              fontWeight="medium"
              isDisabled={credits !== null && credits < 0.01}
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to add a phone number"
                  : undefined
              }
            >
              Add Phone Number
            </MenuButton>

            <MenuList bg="white" borderColor="gray.300">
              {canBuyNumbers && (
                <MenuItem
                  icon={<ShoppingCart size={16} />}
                  onClick={onCreateDialogOpen}
                  _hover={{ bg: "gray.100" }}
                >
                  Buy New Number
                </MenuItem>
              )}

              <MenuItem
                icon={<Cable size={16} />}
                onClick={() => {
                  if (agents.length === 0) {
                    toast({
                      title: "No agents found",
                      description: "Please create an agent first",
                      status: "warning",
                      duration: 3000,
                    });
                  } else {
                    onImportDialogOpen();
                  }
                }}
                _hover={{ bg: "gray.100" }}
              >
                Import via SIP Trunking
              </MenuItem>

              <MenuItem
                icon={<Cable size={16} />}
                onClick={onConnectOpen}
                _hover={{ bg: "gray.100" }}
              >
                Connect Your Number (BYOT)
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Flex>

      {!isLoading && phoneNumbers.length > 0 && (
        <Grid
          templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          gap={4}
          mb={6}
        >
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Phone Numbers</StatLabel>
                <StatNumber color="black">{totalPhoneNumbers}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Active Inbound</StatLabel>
                <StatNumber color="green.600">{activeInbound}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
          <Card
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-4px) scale(1.02)",
              shadow: "xl",
            }}
          >
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Active Outbound</StatLabel>
                <StatNumber color="blue.600">{activeOutbound}</StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {isLoading ? (
        <Flex
          justify="center"
          align="center"
          h="500px"
          minH="calc(100vh - 80px)"
        >
          <Spinner size="xl" color="primary.500" />
        </Flex>
      ) : phoneNumbers.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          <Phone size={48} color="gray" />
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
            No phone numbers yet
          </Text>
          <Text fontSize="sm" color="gray.500" mt={2}>
            Get started by connecting your Twilio number or importing via SIP trunking
          </Text>
          <HStack spacing={3} mt={6}>
            {canBuyNumbers && (
              <Button
                onClick={onCreateDialogOpen}
                colorScheme="primary"
                bg="primary.800"
                _hover={{ bg: "primary.900" }}
                leftIcon={<ShoppingCart size={16} />}
                isDisabled={credits !== null && credits < 0.01}
                title={
                  credits !== null && credits < 0.01
                    ? "Insufficient balance to buy a phone number"
                    : undefined
                }
              >
                Buy New Number
              </Button>
            )}
            <Button
              onClick={() => {
                if (agents.length === 0) {
                  toast({
                    title: "No agents found",
                    description: "Please create an agent first",
                    status: "warning",
                    duration: 3000,
                  });
                } else {
                  onImportDialogOpen();
                }
              }}
              variant="outline"
              borderColor="gray.300"
              color="primary.800"
              _hover={{ bg: "gray.100" }}
              leftIcon={<Cable size={16} />}
              isDisabled={credits !== null && credits < 0.01}
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to import a phone number"
                  : undefined
              }
            >
              Import via SIP
            </Button>
          </HStack>
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fill, minmax(400px, 1fr))" gap={6}>
          {phoneNumbers.map((phoneNumber) => (
            <Card
              role="button"
              tabIndex={credits !== null && credits < 0.01 ? -1 : 0}
              aria-disabled={credits !== null && credits < 0.01}
              key={phoneNumber.phone_number}
              borderLeft="4px"
              borderLeftColor="primary.500"
              cursor={
                credits !== null && credits < 0.01 ? "not-allowed" : "pointer"
              }
              onClick={() => {
                if (credits !== null && credits < 0.01) return;
                handlePhoneNumberClick(phoneNumber);
              }}
              _hover={credits !== null && credits < 0.01 ? {} : { shadow: "md" }}
              height="250px"
              display="flex"
              flexDirection="column"
              opacity={credits !== null && credits < 0.01 ? 0.5 : 1}
              transition="all 0.2s"
              title={
                credits !== null && credits < 0.01
                  ? "Insufficient balance to view phone number details"
                  : undefined
              }
            >
              <CardBody display="flex" flexDirection="column" p={5}>
                <VStack align="stretch" spacing={3} flex={1}>
                  {/* Header */}
                  <Flex justify="space-between" align="start">
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="lg" fontWeight="bold" color="black">
                        {getVoicePhoneCardTitle(phoneNumber)}
                      </Text>
                      {shouldShowVoicePhoneCardSubtitle(phoneNumber) ? (
                        <Text fontSize="sm" color="gray.500">
                          {getDisplayPhonePretty(phoneNumber)}
                        </Text>
                      ) : null}
                    </VStack>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Make call"
                        icon={<PhoneCall size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="green"
                        onClick={(e) => handleCallClick(phoneNumber, e)}
                        isDisabled={!hasOutboundAgent(phoneNumber)}
                      />
                      <IconButton
                        aria-label="Delete"
                        icon={<Trash2 size={16} />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={(e) => handleDeleteClick(phoneNumber, e)}
                      />
                    </HStack>
                  </Flex>

                  {/* Agent assignments */}
                  <VStack
                    align="stretch"
                    spacing={2}
                    pt={2}
                    borderTop="1px solid"
                    borderColor="gray.100"
                  >
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Inbound Agent:
                      </Text>
                      <Badge
                        colorScheme={
                          primaryInboundAgentId(phoneNumber) ? "green" : "gray"
                        }
                        fontSize="xs"
                      >
                        {getAgentName(primaryInboundAgentId(phoneNumber))}
                      </Badge>
                    </Flex>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="sm" color="gray.600">
                        Outbound Agent:
                      </Text>
                      <Badge
                        colorScheme={
                          primaryOutboundAgentId(phoneNumber) ? "blue" : "gray"
                        }
                        fontSize="xs"
                      >
                        {getAgentName(primaryOutboundAgentId(phoneNumber))}
                      </Badge>
                    </Flex>
                  </VStack>

                  {/* Provider info - also make the first letter uppercase */}
                  <Text
                    fontSize="xs"
                    color="gray.400"
                    pt={2}
                    borderTop="1px solid"
                    borderColor="gray.100"
                    mt="auto"
                  >
                    Provider:{" "}
                    {(() => {
                      const domain = process.env.REACT_APP_DOMAIN_NAME || "";
                      const parts = domain.split(".");
                      if (parts.length === 2) {
                        return (
                          parts[0].charAt(0).toUpperCase() + parts[0].slice(1)
                        );
                      } else if (parts.length === 3) {
                        return (
                          parts[1].charAt(0).toUpperCase() + parts[1].slice(1)
                        );
                      } else {
                        return "Custom Telephony";
                      }
                    })()}
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}

      <CreatePhoneNumberDialog
        isOpen={isCreateDialogOpen}
        onClose={onCreateDialogClose}
        onSuccess={handlePhoneNumberChange}
      />

      <ImportPhoneNumberDialog
        isOpen={isImportDialogOpen}
        onClose={onImportDialogClose}
        onSuccess={handlePhoneNumberChange}
      />

      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Phone Number Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedPhoneNumber && (
              <PhoneNumberDetails
                phoneNumber={selectedPhoneNumber}
                agents={agents.map((agent) => ({
                  id: agent.agent_id,
                  name: agent.agent_name || agent.agent_id,
                }))}
                onUpdate={handlePhoneNumberChange}
                onDelete={() => {
                  onEditModalClose();
                  handlePhoneNumberChange();
                }}
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Phone Number
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>
                {phoneNumberToDelete
                  ? getVoicePhoneCardTitle(phoneNumberToDelete)
                  : ""}
              </strong>
              ? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={onDeleteAlertClose}
                variant="ghost"
                color="black"
                _hover={{ bg: "gray.100" }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteConfirm}
                ml={3}
                colorScheme="red"
                bg="red.500"
                _hover={{ bg: "red.600" }}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <ConnectVoiceNumberDialog
        isOpen={isConnectOpen}
        onClose={onConnectClose}
        onSuccess={loadData}
      />

      {phoneNumberToCall && (
        <MakeOutboundCallDialog
          isOpen={isCallDialogOpen}
          onClose={onCallDialogClose}
          phoneNumber={phoneNumberToCall.phone_number}
          agentId={primaryOutboundAgentId(phoneNumberToCall)}
        />
      )}
    </Box>
  );
};

export default VoicePhoneNumbers;
